/**
 * Accept Invitation Page
 * Handles invitation acceptance for both new and existing users.
 * Route: /accept-invitation/:token (public route)
 *
 * Flow:
 * 1. Fetch invitation details (public endpoint)
 * 2. If existing user: Show accept button + optional listing transfer
 * 3. If new user: Show registration form (name, password)
 * 4. On accept: Create account (if new) and join company
 */

import { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { CompanyContext } from '../contexts/CompanyContext';
import { useToast } from '../components/common/ToastProvider';
import {
  getInvitationDetails,
  acceptInvitation,
  type InvitationDetails,
  type AcceptInvitationResponse,
} from '../services/teamService';
import { ApiError } from '../utils/apiClient';
import RoleBadge from '../components/team/RoleBadge';
import Button from '../components/base/Button';
import Navigation from '../components/feature/Navigation';
import Footer from '../components/feature/Footer';
import { GooeyLoader } from '../components/ui/gooey-loader';

type PageState =
  | 'loading'
  | 'valid'
  | 'expired'
  | 'invalid'
  | 'success'
  | 'error'
  | 'already-member';

export default function AcceptInvitationPage() {
  const { t, i18n } = useTranslation('team');
  const isRTL = i18n.language === 'ar';
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const companyContext = useContext(CompanyContext);
  const { showToast } = useToast();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isAccepting, setIsAccepting] = useState(false);

  // Registration form state (for new users)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [transferListings, setTransferListings] = useState(true);

  // Form validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Fetch invitation details on mount
  const fetchInvitation = useCallback(async () => {
    if (!token) {
      setPageState('invalid');
      return;
    }

    try {
      const details = await getInvitationDetails(token);
      setInvitation(details);
      setPageState('valid');
    } catch (err) {
      console.error('Failed to fetch invitation:', err);

      if (err instanceof ApiError) {
        const code = (err as ApiError & { code?: string }).code;
        const message = err.message.toLowerCase();

        if (code === 'INVITATION_EXPIRED' || message.includes('expired')) {
          setPageState('expired');
        } else if (code === 'INVITATION_ACCEPTED' || message.includes('accepted')) {
          setPageState('already-member');
        } else if (code === 'NOT_FOUND' || message.includes('not found')) {
          setPageState('invalid');
        } else {
          setPageState('error');
          setErrorMessage(err.message);
        }
      } else {
        setPageState('error');
        setErrorMessage(t('acceptInvitation.genericError'));
      }
    }
  }, [token, t]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  // Determine if this is a new user (no account with invited email)
  const isNewUser = invitation && !invitation.existingUser;
  const isLoggedInAsDifferentUser = Boolean(user && invitation && user.email !== invitation.email);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (isNewUser) {
      if (!firstName.trim()) {
        errors.firstName = t('acceptInvitation.validation.firstNameRequired');
      }
      if (!lastName.trim()) {
        errors.lastName = t('acceptInvitation.validation.lastNameRequired');
      }
      if (!password) {
        errors.password = t('acceptInvitation.validation.passwordRequired');
      } else if (password.length < 8) {
        errors.password = t('acceptInvitation.validation.passwordTooShort');
      }
      if (password !== confirmPassword) {
        errors.confirmPassword = t('acceptInvitation.validation.passwordMismatch');
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAcceptInvitation = async () => {
    if (!token || !invitation) return;

    if (!validateForm()) return;

    setIsAccepting(true);
    setErrorMessage('');

    try {
      const payload = isNewUser
        ? { firstName, lastName, password }
        : { transferListings };

      const response: AcceptInvitationResponse = await acceptInvitation(
        token,
        payload,
        session?.token
      );

      // For new users, redirect to sign-in after successful registration
      // The session will be established when they sign in
      if (response.isNewUser && response.accessToken) {
        // New user account created - they can now sign in
        console.log('New user account created, redirect to sign-in');
      }

      // Refresh company context so the new membership is reflected
      if (companyContext?.refetch) {
        await companyContext.refetch();
      }

      setPageState('success');
      showToast(
        t('acceptInvitation.successTitle', { company: invitation.companyName }),
        'success'
      );
    } catch (err) {
      console.error('Accept invitation error:', err);

      if (err instanceof ApiError) {
        const message = err.message.toLowerCase();

        if (message.includes('expired')) {
          setPageState('expired');
        } else if (message.includes('invalid') || message.includes('not found')) {
          setPageState('invalid');
        } else if (message.includes('already') || message.includes('member')) {
          setPageState('already-member');
        } else {
          setPageState('error');
          setErrorMessage(err.message);
        }
      } else {
        setPageState('error');
        setErrorMessage(t('acceptInvitation.genericError'));
      }
    } finally {
      setIsAccepting(false);
    }
  };

  const handleGoToTeam = () => {
    navigate('/company/team');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleLogout = () => {
    // Store current URL to redirect back after sign in
    const returnUrl = `/accept-invitation/${token}`;
    navigate(`/signin?returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20">
          <div className="max-w-md w-full mx-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 flex items-center justify-center">
                <GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" />
              </div>
              <p className="text-gray-600 dark:text-gray-400">{t('acceptInvitation.verifying')}</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Valid invitation - show accept UI
  if (pageState === 'valid' && invitation) {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="max-w-md w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 flex items-center justify-center">
                  <i className="ri-building-line text-3xl text-[#004D40]"></i>
                </div>
                <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
                  {t('acceptInvitation.title')}
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300 font-medium">
                  {invitation.companyName}
                </p>
              </div>

              {/* Invitation details */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('acceptInvitation.email')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {invitation.email}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('acceptInvitation.role')}</span>
                  <RoleBadge role={invitation.role} size="md" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('acceptInvitation.invitedBy')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {invitation.invitedByName}
                  </span>
                </div>
              </div>

              {/* Warning if logged in as different user */}
              {isLoggedInAsDifferentUser && (
                <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('acceptInvitation.differentUser', { email: user?.email })}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline"
                  >
                    {t('acceptInvitation.switchAccount')}
                  </button>
                </div>
              )}

              {/* New user registration form */}
              {isNewUser && (
                <div className="mb-6 space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('acceptInvitation.createAccount')}
                  </p>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('acceptInvitation.firstName')}
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40] dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                          formErrors.firstName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('acceptInvitation.firstNamePlaceholder')}
                      />
                      {formErrors.firstName && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('acceptInvitation.lastName')}
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40] dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                          formErrors.lastName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder={t('acceptInvitation.lastNamePlaceholder')}
                      />
                      {formErrors.lastName && (
                        <p className="mt-1 text-xs text-red-500">{formErrors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('acceptInvitation.password')}
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40] dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        formErrors.password ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('acceptInvitation.passwordPlaceholder')}
                    />
                    {formErrors.password && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.password}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('acceptInvitation.confirmPassword')}
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#004D40] dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                        formErrors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={t('acceptInvitation.confirmPasswordPlaceholder')}
                    />
                    {formErrors.confirmPassword && (
                      <p className="mt-1 text-xs text-red-500">{formErrors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Existing user with listings - transfer option */}
              {!isNewUser && invitation.existingUser && invitation.existingUser.listingCount > 0 && (
                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={transferListings}
                      onChange={(e) => setTransferListings(e.target.checked)}
                      className="mt-1 h-4 w-4 text-[#004D40] border-gray-300 rounded focus:ring-[#004D40]"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('acceptInvitation.transferListings')}
                      </span>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {t('acceptInvitation.transferListingsDesc', {
                          count: invitation.existingUser.listingCount,
                          company: invitation.companyName,
                        })}
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Accept button */}
              <Button
                onClick={handleAcceptInvitation}
                disabled={isAccepting || isLoggedInAsDifferentUser}
                className="w-full"
              >
                {isAccepting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {t('acceptInvitation.accepting')}
                  </>
                ) : isNewUser ? (
                  <>
                    <i className="ri-user-add-line mr-2"></i>
                    {t('acceptInvitation.createAndJoin')}
                  </>
                ) : (
                  <>
                    <i className="ri-check-line mr-2"></i>
                    {t('acceptInvitation.acceptButton')}
                  </>
                )}
              </Button>

              {/* Disclaimer */}
              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('acceptInvitation.acceptDisclaimer', {
                  role: t(`role${invitation.role.charAt(0) + invitation.role.slice(1).toLowerCase()}`),
                  company: invitation.companyName,
                })}
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
          <div className="max-w-md w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
                {t('acceptInvitation.successTitle', { company: invitation?.companyName || '' })}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('acceptInvitation.successMessage', {
                  role: invitation?.role
                    ? t(`role${invitation.role.charAt(0) + invitation.role.slice(1).toLowerCase()}`)
                    : '',
                })}
              </p>
              <Button onClick={handleGoToTeam} className="w-full">
                <i className="ri-team-line mr-2"></i>
                {t('acceptInvitation.goToTeam')}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Expired state
  if (pageState === 'expired') {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
          <div className="max-w-md w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <i className="ri-time-line text-3xl text-amber-600"></i>
              </div>
              <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
                {t('acceptInvitation.expiredTitle')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('acceptInvitation.expiredMessage')}
              </p>
              <Button onClick={handleGoHome} variant="secondary" className="w-full">
                <i className="ri-home-line mr-2"></i>
                {t('acceptInvitation.goHome')}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Invalid state
  if (pageState === 'invalid') {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
          <div className="max-w-md w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <i className="ri-error-warning-line text-3xl text-red-600"></i>
              </div>
              <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
                {t('acceptInvitation.invalidTitle')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('acceptInvitation.invalidMessage')}
              </p>
              <Button onClick={handleGoHome} variant="secondary" className="w-full">
                <i className="ri-home-line mr-2"></i>
                {t('acceptInvitation.goHome')}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Already member state
  if (pageState === 'already-member') {
    return (
      <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
          <div className="max-w-md w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
              <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <i className="ri-user-star-line text-3xl text-blue-600"></i>
              </div>
              <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
                {t('acceptInvitation.alreadyMemberTitle')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('acceptInvitation.alreadyMember')}
              </p>
              <Button onClick={handleGoToTeam} className="w-full">
                <i className="ri-team-line mr-2"></i>
                {t('acceptInvitation.goToTeam')}
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-white dark:from-gray-900 dark:to-gray-950">
      <Navigation />
      <div className="flex items-center justify-center min-h-[calc(100vh-80px)] pt-20 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-red-600"></i>
            </div>
            <h2 className="text-xl font-bold text-[var(--pv-color-primary)] dark:text-white mb-2">
              {t('acceptInvitation.errorTitle')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {errorMessage || t('acceptInvitation.genericError')}
            </p>
            <div className="space-y-3">
              <Button onClick={fetchInvitation} className="w-full">
                <i className="ri-refresh-line mr-2"></i>
                {t('acceptInvitation.tryAgain')}
              </Button>
              <Button onClick={handleGoHome} variant="secondary" className="w-full">
                <i className="ri-home-line mr-2"></i>
                {t('acceptInvitation.goHome')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
